import {
  STORE_CATEGORIES,
  STORE_CATEGORIES_PARENTS,
  STORE_TAGS,
} from '@bounty/constants'
import { GetStoresResponseSuccess } from '@bounty/types'
import useSWRInfinite from 'swr/infinite'
import { z } from 'zod'
import { GetStoresParams } from '@bounty/schemas'
import { useShopListContext } from './components/ShopListContext'
import { SORT_ORDERS } from './components/const'
import { createParam } from 'solito'
import { buildShopCategoryGroupings } from './components/buildShopFilters'
import { useSWRBounty } from '../../hooks/swr'
import { CreatorsClient } from '../../libs/creators'

const { useParam } = createParam()

const useFilteringParams = () => {
  const { data } = useSWRBounty('getStorefrontFilters')

  const [parentCategory] = useParam('parentCategory')
  const [tag] = useParam('tag')
  const [category] = useParam('category')

  if (!data) {
    return null
  }

  if (category) {
    const maybeCategory = STORE_CATEGORIES.find(
      (x) =>
        x.slug === category &&
        parentCategory ===
          STORE_CATEGORIES_PARENTS.find((parent) => parent.value === x.parent)
            ?.slug,
    )
    if (!maybeCategory) {
      throw new Error('Cannot find category for store')
    }
    return { categories: [maybeCategory.value] }
  }

  if (tag) {
    const maybeTag = STORE_TAGS.find((x) => x.slug === tag)
    if (!maybeTag) {
      throw new Error('Cannot find tag for store')
    }
    return { tags: [maybeTag.value] }
  }

  if (parentCategory) {
    const maybeParentCategory = STORE_CATEGORIES_PARENTS.find(
      (x) => x.slug === parentCategory,
    )
    if (!maybeParentCategory) {
      throw new Error(
        `Cannot find parent category for key: ${parentCategory}. This could be because there are no active stores for this category grouping.`,
      )
    }

    const categoryGroupings = buildShopCategoryGroupings({
      categories: data.categories,
    })

    const maybeCategoryGrouping = categoryGroupings.find(
      (x) => x.value === maybeParentCategory.value,
    )
    if (!maybeCategoryGrouping) {
      throw new Error('No active categories are active for parent!')
    }

    return {
      // Some categories do not have children, in that case we just look for value (e.x. gender neutral)
      // Other categories like home has multiple categories so we query for all of the children
      //
      // We never query directly for a parent because there is not parent child relationship codified in the DB
      // and we need to keep it that way to reduce confusion! There was parent child in Sanity and it became a huge mess.
      categories: maybeCategoryGrouping.children
        ? maybeCategoryGrouping.children.map((x) => x.value)
        : [maybeCategoryGrouping.value],
    }
  }

  return { categories: [], tags: [] }
}

export const useShopList = () => {
  const filteringParams = useFilteringParams()
  const [{ sortOrder }] = useShopListContext()
  const { data: countData } = useSWRBounty('getStoresCount')
  const { data, size, setSize, isLoading, error } = useSWRInfinite<
    GetStoresResponseSuccess['data']
  >(
    (pageIndex: number) => {
      if (countData != null) {
        const newSkip = pageIndex * 20

        if (newSkip > countData.count) {
          return null
        }

        const sortConfig = SORT_ORDERS.find((x) => x.value === sortOrder)

        if (!sortConfig) {
          throw new Error(`Cannot find sort config for ${sortOrder}`)
        }

        if (!filteringParams) {
          throw new Error('Not ready because filtering params!')
        }

        const params: z.input<typeof GetStoresParams> = {
          ...(filteringParams.categories &&
            filteringParams.categories.length > 0 && {
              categories: filteringParams.categories.join(','),
            }),
          ...(filteringParams.tags &&
            filteringParams.tags.length > 0 && {
              tags: filteringParams.tags.join(','),
            }),
          skip: `${newSkip}`,
          take: '20',
          orderBy: sortConfig.orderBy,
          orderByDirection: sortConfig.orderByDirection,
        }

        return CreatorsClient.produceCacheKey('getStores', params)
      }

      // Throw to make query dependent
      // https://swr.vercel.app/docs/conditional-fetching#dependent
      throw new Error('Not ready!')
    },
    (fetcherArgs) => {
      const [, params] = JSON.parse(fetcherArgs.replace('$inf$', ''))
      const client = new CreatorsClient()

      return client.getStores(params)
    },
    {
      revalidateFirstPage: false,
    },
  )

  const flattened =
    data
      ?.map((x) => x.items)
      .filter(Boolean)
      .flat() ?? []

  // Filter for Bonnie Plants
  const bonniePlantsFilter = {
    category: 'garden_and_patio',
    tags: ['in_store', 'TIKTOK_VIDEO', 'INSTAGRAM_REEL'],
  }

  const isSelectedFilterEmpty =
    filteringParams?.categories?.length === 0 &&
    filteringParams.tags?.length === 0
  const isSelectedFilterMatched =
    (filteringParams?.categories as string[])?.includes(
      bonniePlantsFilter.category,
    ) ||
    (filteringParams?.tags as string[])?.some((tag) =>
      bonniePlantsFilter.tags.includes(tag),
    )

  // if (isSelectedFilterEmpty || isSelectedFilterMatched) {
  //   // Bonnie Plants data
  //   flattened.unshift({
  //     name: 'Bonnie Plants',
  //     logo: 'https://shop-assets.bounty.co/ada3153f-c460-4b2f-9865-e9240bec80fc/logo',
  //     slug: 'bonnie-plants',
  //     redirectUrl: 'https://www.bounty.co/s/bonnie-plants',
  //     shopUrl: 'https://www.bounty.co/s/bonnie-plants',
  //     id: 'ada3153f-c460-4b2f-9865-e9240bec80fc',
  //     featured: true,
  //     deals: [],
  //     headerImgLinkPrimary:
  //       'https://shop-assets.bounty.co/ada3153f-c460-4b2f-9865-e9240bec80fc/storefront-header-image-primary',
  //     headerImgLinkSecondary:
  //       'https://shop-assets.bounty.co/ada3153f-c460-4b2f-9865-e9240bec80fc/storefront-header-image-secondary',
  //     minBountyPayment: 0,
  //     startingCPM: 30,
  //     startingCPMViews: 1000,
  //     bountyLimitPerMonth: 5,
  //     contentTypesAllowed: ['TIKTOK_VIDEO', 'INSTAGRAM_REEL'],
  //   })
  // }

  /**
   * NOTE: keep this in sync with ShopList.tsx
   */
  /*
  flattened.unshift({
    name: 'Pantene',
    logo: 'https://shop-assets.bounty.co/7e7255ca-1706-4774-92a7-a831bde224c2/logo',
    slug: 'pantene',
    redirectUrl: 'https://www.bounty.co/s/pentene',
    shopUrl: 'https://www.bounty.co/s/pantene',
    id: '7e7255ca-1706-4774-92a7-a831bde224c2',
    featured: true,
    deals: [],
    headerImgLinkPrimary:
      'https://shop-assets.bounty.co/7e7255ca-1706-4774-92a7-a831bde224c2/storefront-header-image-primary',
    headerImgLinkSecondary:
      'https://shop-assets.bounty.co/7e7255ca-1706-4774-92a7-a831bde224c2/storefront-header-image-secondary',
    minBountyPayment: 0,
    startingCPM: 30,
    startingCPMViews: 1000,
    bountyLimitPerMonth: 5,
    contentTypesAllowed: ['TIKTOK_VIDEO', 'INSTAGRAM_REEL'],
  })
*/

  /*
  flattened.unshift({
    name: 'Hair Food',
    logo: 'https://shop-assets.bounty.co/d4ccfadd-2d25-40fa-8dcf-9927ccc0f483/logo',
    slug: 'hair-food',
    redirectUrl: 'https://www.bounty.co/s/hair-food',
    shopUrl: 'https://www.bounty.co/s/hair-food',
    id: 'd4ccfadd-2d25-40fa-8dcf-9927ccc0f483',
    featured: true,
    deals: [],
    headerImgLinkPrimary:
      'https://shop-assets.bounty.co/d4ccfadd-2d25-40fa-8dcf-9927ccc0f483/storefront-header-image-primary',
    headerImgLinkSecondary:
      'https://shop-assets.bounty.co/d4ccfadd-2d25-40fa-8dcf-9927ccc0f483/storefront-header-image-secondary',
    minBountyPayment: 0,
    startingCPM: 30,
    startingCPMViews: 1000,
    bountyLimitPerMonth: 5,
    contentTypesAllowed: ['TIKTOK_VIDEO', 'INSTAGRAM_REEL'],
  })
*/

  /*
  flattened.unshift({
    name: 'Nike',
    logo: 'https://shop-assets.bounty.co/476b8dd8-883f-475a-9b41-8e202b139b00/logo',
    slug: 'nike',
    redirectUrl: 'https://www.bounty.co/s/nike',
    shopUrl: 'https://www.bounty.co/s/nike',
    id: '7e7255ca-1706-4774-92a7-a831bde224c2',
    featured: true,
    deals: [],
    headerImgLinkPrimary:
      'https://shop-assets.bounty.co/476b8dd8-883f-475a-9b41-8e202b139b00/storefront-header-image-primary',
    headerImgLinkSecondary:
      'https://shop-assets.bounty.co/476b8dd8-883f-475a-9b41-8e202b139b00/storefront-header-image-secondary',
    minBountyPayment: 10,
    startingCPM: 4,
    startingCPMViews: 1000,
    bountyLimitPerMonth: 5,
    contentTypesAllowed: ['TIKTOK_VIDEO', 'INSTAGRAM_REEL'],
  })
*/

  const hasNextPage = countData != null && countData.count > flattened.length
  return {
    items: flattened,
    size,
    setSize,
    isLoading,
    error,
    hasNextPage,
  }
}
